import { type LocationQuery } from 'vue-router';
import { type BusPackageLoaderConfig } from './types';
import { type BusPathData } from '@search/trip/bus/types';
import {
    type BusLocation,
    type BusUrlParams,
    getLocationCode,
} from '@utils/bus';
import type Bus from '@itinerary/trip/bus/Bus';
import type BusPriceClass from '@itinerary/trip/bus/BusPriceClass';
import { TaxiLateralLoader } from '@itinerary/trip/bus/TaxiLateralLoader';
import type TripPackage from '@itinerary/trip/TripPackage';
import { GetNearbyRoutesApiApiGetNearbyRoutesPost } from '@clients/BusApi';
import BusPackage from '@itinerary/trip/bus/BusPackage';
import {
    TimeFilter,
    type TripShareInfo,
    TripSortBy,
} from '@itinerary/trip/TripModuleTypes';
import {
    ButtonGroupFilter,
    CheckboxFilter,
    type Filter,
    LoadingStatus,
    PriceFilter,
    type SortBy,
} from '@itinerary/ItineraryModuleTypes';
import {
    BusFilters,
    type BusFiltersData,
} from '@itinerary/trip/bus/BusModuleTypes';
import TripModule from '@itinerary/trip/TripModule';
import { AutoReserveType, ReserveType } from '@utils/itinerary';
import { TripType } from '@utils/trip';
import BusPackageSet from '@itinerary/trip/bus/BusPackageSet';
import TaxiPackageSet from '@itinerary/trip/bus/TaxiPackageSet';

export default class BusModule extends TripModule<
    Bus,
    BusPriceClass,
    BusFiltersData,
    BusLocation
> {
    constructor(
        public descriptionTemplate = 'بلیط اتوبوس (مبدا) (مقصد), به صرفه‌ترین روش برای رزرو آنلاین بلیط اتوبوس (مبدا) به مقصد (مقصد) سریع، ارزان، راحت و ایمن با سامانه‌ی هوشمند وب سایت مِستربلیط، قیمت‌های ارزان و پشتیبانی کامل را به شما تضمین می‌دهیم.',
        public reserveType: ReserveType.Bus | ReserveType.Taxi = ReserveType.Bus
    ) {
        super();
    }

    override get defaultAutoReserveType() {
        return this.reserveType === ReserveType.Bus
            ? AutoReserveType.Route
            : AutoReserveType.None;
    }

    override getPackageSet(
        loaderConfig: BusPackageLoaderConfig
    ): BusPackageSet | TaxiPackageSet {
        const PackageSetClass =
            this.reserveType === ReserveType.Bus
                ? BusPackageSet
                : TaxiPackageSet;
        return new PackageSetClass(loaderConfig, this.getInitialFilters());
    }

    override get reserveInfo() {
        if (this.reserveType === ReserveType.Bus) {
            return useBusForm().reserveInfo;
        } else {
            return useTaxiForm().reserveInfo;
        }
    }

    override get emptyPackage() {
        const busForm = useBusForm();
        return new BusPackage(
            {
                id: 42562610,
                from: Number(busForm.originCode),
                to: Number(busForm.destinationCode),
                fromName: this.originName,
                toName: this.destinationName,
                fromCity: this.originName,
                toCity: this.destinationName,
                departureTime: busForm.departureDate?.toISOString(),
                capacity: 0,
                superCorporation: 'مِستربلیط',
            },
            busForm.adultCount,
            false,
            ReserveType.Bus
        );
    }

    override get showSearchSuggestion(): boolean {
        return true;
    }

    override get extraAutoReserveItem() {
        if (
            this.autoReservableList?.[0] &&
            this.sortedReservableList.length >= 4
        ) {
            return this.autoReservableList?.[0];
        }
        return null;
    }

    override get defaultSortBy(): TripSortBy {
        return this.reserveType === ReserveType.Taxi
            ? TripSortBy.MrbilitOffer
            : TripSortBy.Time;
    }

    override get sortByList(): SortBy[] {
        return [
            ...(this.reserveType === ReserveType.Taxi
                ? [
                      {
                          type: TripSortBy.MrbilitOffer,
                          label: 'پیشنهاد مِستربلیط',
                      },
                  ]
                : []),
            {
                type: TripSortBy.Time,
                label: 'زود‌ترین زمان',
            },
            {
                type: TripSortBy.Price,
                label: 'کمترین قیمت',
            },
        ];
    }

    override applySort(list: BusPackage[]): BusPackage[] {
        if (this.sortBy === TripSortBy.MrbilitOffer) {
            return list;
        } else {
            return super.applySort(list) as BusPackage[];
        }
    }

    override async terminatePackageReserve(_reserveId: number): Promise<void> {
        // this has problem with webapp, where the application is not restarted on after-order,
        // and may have errors somewhere else, where we hasn't catch yet.
        // so we decided to disable it temporarily
        // await CancelSeatReserveApiApiCancelSeatReserveGet({
        //     reserveId,
        // });
    }

    override get lateralLoader(): TaxiLateralLoader | null {
        return this.reserveType === ReserveType.Bus
            ? new TaxiLateralLoader()
            : null;
    }

    override applyFilter(
        pkg: TripPackage<Bus, BusPriceClass>,
        filters: { [key in BusFiltersData['key']]: any }
    ): boolean {
        if (!pkg.segments.length) {
            return false;
        }

        const [bus] = pkg.segments[0].trips;
        if (!bus) {
            return false;
        }

        const departureMinutes = timeToMinutes(bus.departureTime);
        const arrivalMinutes =
            bus.arrivalTime && timeToMinutes(bus.arrivalTime);
        return (
            (!filters.carrier ||
                filters.carrier.length === 0 ||
                filters.carrier.includes(
                    bus.corporationInfo.code.toString()
                )) &&
            (!filters.price ||
                (pkg.singlePayable >= filters.price[0] &&
                    pkg.singlePayable <= filters.price[1])) &&
            (!filters.time ||
                !filters.time.departure ||
                filters.time.departure.length === 0 ||
                (departureMinutes <= 8 * 60 &&
                    filters.time.departure.includes('midnight')) ||
                (departureMinutes >= 8 * 60 &&
                    departureMinutes <= 18 * 60 &&
                    filters.time.departure.includes('day')) ||
                (departureMinutes >= 18 * 60 &&
                    filters.time.departure.includes('night'))) &&
            (!filters.time ||
                !filters.time.arrival ||
                filters.time.arrival.length === 0 ||
                (+arrivalMinutes <= 8 * 60 &&
                    filters.time.arrival.includes('midnight')) ||
                (+arrivalMinutes >= 8 * 60 &&
                    +arrivalMinutes <= 18 * 60 &&
                    filters.time.arrival.includes('day')) ||
                (+arrivalMinutes >= 18 * 60 &&
                    filters.time.arrival.includes('night'))) &&
            (!filters.type ||
                filters.type.length === 0 ||
                (bus.isVip && filters.type.includes('vip')) ||
                (!bus.isVip && filters.type.includes('normal')))
        );
    }

    override get filters(): Filter[] {
        if (!this.currentPackageSet || !this.currentPackageSet.filterMetaData) {
            return [];
        }
        const busForm = useBusForm();

        const filterData = this.currentPackageSet.filterMetaData;
        return this.sortFilters([
            new PriceFilter(
                BusFilters.Price,
                filterData.minPrice,
                filterData.maxPrice
            ),
            new TimeFilter(
                BusFilters.Time,
                busForm.origin?.title ?? '',
                busForm.destination?.title ?? ''
            ),
            new ButtonGroupFilter(
                BusFilters.BusType,
                [
                    {
                        id: 'normal',
                        title: 'عادی',
                    },
                    {
                        id: 'vip',
                        title: 'VIP',
                    },
                ],
                this.reserveType === 'bus' ? 'نوع اتوبوس' : 'نوع سواری'
            ),
            new CheckboxFilter(
                BusFilters.Carrier,
                filterData.corporations.map(c => ({
                    title: c.name,
                    id: c.id.toString(),
                })),
                'شرکت مسافربری'
            ),
        ]);
    }

    override get metaTagTitle(): string {
        const all = '(همه پایانه‌ها)';
        return `خرید بلیط ${this.reserveInfo.title} ${this.originName.replace(all, '')} ${this.destinationName.replace(all, '')} با بهترین قیمت${this.originName.includes(all) || this.destinationName.includes(all) ? ` ${all}` : ''} - مِستربلیط`;
    }

    override get currentPackageSet():
        | BusPackageSet
        | TaxiPackageSet
        | null
        | undefined {
        return this.itineraryPackageSets[this.currentSetIndex] as
            | BusPackageSet
            | TaxiPackageSet
            | null
            | undefined;
    }

    override async loadAvailable(params: { index: number }) {
        const promise = super.loadAvailable(params);
        const busForm = useBusForm();
        if (this.currentPackageSet) {
            busForm.setOriginBaseOnCode(
                this.isSelectingReturn
                    ? this.currentPackageSet.destinationTerminalCode
                    : this.currentPackageSet.originTerminalCode
            );
            busForm.setDestinationBaseOnCode(
                this.isSelectingReturn
                    ? this.currentPackageSet.originTerminalCode
                    : this.currentPackageSet.destinationTerminalCode
            );
        }
        busForm.loadDestinations();

        await promise;
    }

    override async loadSuggestions(params: TripShareInfo) {
        const busForm =
            this.reserveType === ReserveType.Bus ? useBusForm() : useTaxiForm();
        const { data } = await GetNearbyRoutesApiApiGetNearbyRoutesPost({
            getNearbyRoutesParameters: {
                from: {
                    latitude: params.origin.latitude,
                    longitude: params.origin.longitude,
                },
                to: {
                    latitude: params.destination.latitude,
                    longitude: params.destination.longitude,
                },
                departureDate: dayConfig
                    .gregory(params.departureDate)
                    .format(formatter()),
                providerId: busForm.specificProviderId,
            },
        });

        const route = data.routes?.[0];
        if (!route?.fromID || !route.toID) {
            this.setPackageSet({
                index: this.currentSetIndex,
                set: null,
            });
            return;
        }
        let totalSeatCount = params.adultCount + params.childCount;

        if (this.reserveType === ReserveType.Taxi) {
            totalSeatCount = Math.min(totalSeatCount, 4);
        }
        const originCode = getLocationCode({
            id: route.fromID,
            title: route.fromName ?? '',
            persianTitle: route.fromName ?? '',
            englishTitle: route.fromEnglishName ?? null,
            code: route.fromCode ?? '',
        });
        const destinationCode = getLocationCode({
            id: route.toID,
            title: route.toName ?? '',
            persianTitle: route.toName ?? '',
            englishTitle: route.toEnglishName ?? null,
            code: route.toCode ?? '',
        });
        busForm.setOrigin({
            code: originCode,
            title: route.fromName ?? '',
            terminalId: route.fromID,
        });
        busForm.setDestination({
            code: destinationCode,
            title: route.toName ?? '',
            terminalId: route.toID,
        });
        busForm.setTripTypeAndCheckDate(params.tripType);
        const url = busForm.generateViewUrl({
            origin: this.isSelectingReturn ? route.toCode! : route.fromCode!,
            destination: this.isSelectingReturn
                ? route.fromCode!
                : route.toCode!,
            departureDate: dayConfig
                .jalali(params.departureDate)
                .format(formatter()),
            returnDate:
                params.tripType === TripType.RoundTrip
                    ? dayConfig.jalali(params.returnDate).format(formatter())
                    : undefined,
            adultCount: totalSeatCount.toString(),
        });

        const PackageSetClass =
            this.reserveType === ReserveType.Bus
                ? BusPackageSet
                : TaxiPackageSet;

        this.setPackageSet({
            index: this.currentSetIndex,
            set: route
                ? new PackageSetClass(
                      {
                          originCode,
                          destinationCode,
                          originTerminalId: route.fromID,
                          destinationTerminalId: route.toID,
                          date: this.isSelectingReturn
                              ? params.returnDate!
                              : params.departureDate!,
                          totalSeatCount,
                          specificProviderId: busForm.specificProviderId,
                          url,
                          originName: route.fromName ?? '',
                          destinationName: route.toName ?? '',
                      },
                      this.getInitialFilters(),
                      route.minPrice,
                      true
                  )
                : null,
            totalLength: params.expectedPackagesToBeSelected,
        });
    }

    override getItineraryPackageLoaderConfigs(
        params: BusUrlParams
    ): BusPackageLoaderConfig[] {
        const busForm = useBusForm();
        const ret: BusPackageLoaderConfig[] = [
            {
                originCode: params.origin,
                destinationCode: params.destination,
                originTerminalId: params.originTerminalId,
                destinationTerminalId: params.destinationTerminalId,
                date: params.departureDate,
                totalSeatCount: params.adultCount,
                specificProviderId: busForm.specificProviderId,
                url: useRoute().path,
                originName: this.originName,
                destinationName: this.destinationName,
            },
        ];

        if (params.returnDate) {
            ret.push({
                originCode: params.destination,
                destinationCode: params.origin,
                originTerminalId: params.destinationTerminalId,
                destinationTerminalId: params.originTerminalId,
                date: params.returnDate,
                totalSeatCount: params.adultCount,
                specificProviderId: busForm.specificProviderId,
                url: useRoute().path,
            });
        }
        return ret;
    }

    override getInitialFilters(): { [key in BusFiltersData['key']]: any } {
        return {
            price: null,
            carrier: null,
            time: null,
            type: null,
        };
    }

    // override formAutoReserve(): Promise<void> {
    //     return Promise.reject(new Error('Not implemented'));
    // }

    override async formAutoReserve(query: LocationQuery) {
        const busForm = useBusForm();
        const params = await busForm.parsePath(query as BusPathData);
        const itineraryStore = useItineraryStore();
        const searchFormStore = useSearchFormStore();
        await busForm.loadViewUrlParams(params);
        searchFormStore.urlParams = params;
        itineraryStore.initItineraryModule(params);
        searchFormStore.initSearchForm();
        if (this.currentLoadingStatus !== LoadingStatus.Loading) {
            itineraryStore.setPackageSetIndex(0);
            if (!this.isSetLoaded(0)) {
                await this.loadAvailable({
                    index: 0,
                    // inForm: true,
                });
                if (
                    this.itineraryPackageSets[0]?.loadingStatus ===
                    LoadingStatus.Error
                ) {
                    throw new Error('در حال حاضر سرور اتوبوس قطع می‌باشد.');
                }
            }
        }
        const formStore = useFormStore();
        formStore.setCounts({
            adultCount: params.adultCount,
            childCount: 0,
            infantCount: 0,
        });
        const packageSelectStore = usePackageSelectStore();

        const returnBus = query?.returnBus;
        const departureBus = query?.departureBus;
        const departurePackage = this.sortedReservableList.find(
            ({ uniqueId }) => uniqueId === departureBus
        );

        if (departurePackage) {
            this.beforeSelectPackage({
                index: 0,
                package: departurePackage,
            });
            packageSelectStore.setSelectedPackage({
                index: 0,
                package: departurePackage,
            });
        } else {
            throw new Error('اتوبوس یافت نشد!');
        }
        if (
            params.returnDate &&
            returnBus &&
            this.itineraryPackageSets.length > 1
        ) {
            itineraryStore.setPackageSetIndex(1);
            if (!this.isSetLoaded(1)) {
                await this.loadAvailable({
                    index: 1,
                    // inForm: true,
                });
            }
            if (
                this.itineraryPackageSets[1]?.loadingStatus ===
                LoadingStatus.Error
            ) {
                throw new Error('در حال حاضر سرور اتوبوس قطع می‌باشد.');
            }
            const returnPackage = this.sortedReservableList.find(
                ({ uniqueId }) => uniqueId === returnBus
            );
            if (returnPackage) {
                this.beforeSelectPackage({
                    index: 1,
                    package: returnPackage,
                });
                packageSelectStore.setSelectedPackage({
                    index: 1,
                    package: returnPackage,
                });
            } else {
                throw new Error('اتوبوس برگشت یافت نشد!');
            }
        }
        formStore.clearState();
        formStore.setItineraryPackagesAndDepartureTime(
            packageSelectStore.selectedPackages
        );
    }

    override get alternativeSuggestionTypes(): [ReserveType, ReserveType] {
        return [
            this.reserveType === ReserveType.Bus
                ? ReserveType.Taxi
                : ReserveType.Bus,
            ReserveType.Train,
        ];
    }
}
